import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface AdminAuth {
  isAuthenticated: boolean;
  isLoading: boolean;
  pin: string;
  operatorName: string | null;
  operatorId: string | null;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuth>({
  isAuthenticated: false,
  isLoading: true,
  pin: "",
  operatorName: null,
  operatorId: null,
  logout: async () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pin, setPin] = useState("");
  const [operatorName, setOperatorName] = useState<string | null>(null);
  const [operatorId, setOperatorId] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/admin/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.isAdmin) {
          setIsAuthenticated(true);
          setPin(data.pin ?? "");
          setOperatorName(data.operatorName ?? null);
          setOperatorId(data.operatorId ?? null);
          // Keep legacy sessionStorage in sync for backward compat
          if (data.pin) sessionStorage.setItem("admin_pin", data.pin);
          if (data.operatorName) sessionStorage.setItem("operatorName", data.operatorName);
          if (data.operatorId) sessionStorage.setItem("operatorId", String(data.operatorId));
          sessionStorage.setItem("adminAuth", "true");
        } else {
          // Clear any stale legacy state
          sessionStorage.removeItem("adminAuth");
          sessionStorage.removeItem("admin_pin");
        }
      })
      .catch(() => {
        sessionStorage.removeItem("adminAuth");
        sessionStorage.removeItem("admin_pin");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" });
    setIsAuthenticated(false);
    setPin("");
    setOperatorName(null);
    setOperatorId(null);
    sessionStorage.removeItem("adminAuth");
    sessionStorage.removeItem("admin_pin");
    sessionStorage.removeItem("operatorName");
    sessionStorage.removeItem("operatorId");
  }, []);

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, pin, operatorName, operatorId, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}
